import { lazy, Suspense, type FC, type LazyExoticComponent } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
} from "@tanstack/react-router";
import { RootRedirect } from "./pages/index";
import { AmsaasClientLoginPage } from "./pages/auth/amsaasClient";
import { AmcaasPlatformLoginPage } from "./pages/auth/amcaasPlatform";
import {
  ProviderLoginPage,
  DefaultLoginPage,
  AppHostGate,
} from "./pages/auth/dispatch";
import { ProviderLayout } from "./components/layout/ProviderLayout";
import { ClientLayout } from "./components/layout/ClientLayout";
import { NotFoundPage } from "./components/ErrorPage";

/**
 * Route-based code-splitting (TOP-04, GAP-027): page components load lazily, one
 * chunk per route, so the provider (B2B) bundle and the marketplace/platform (B2C)
 * bundle split apart and each host downloads only what it renders. The entry
 * pieces — RootRedirect, LoginPage, the two layouts, NotFoundPage — stay eager.
 */
const page = (
  loader: () => Promise<Record<string, unknown>>,
  name: string,
): LazyExoticComponent<FC> =>
  lazy(() => loader().then((m) => ({ default: m[name] as FC })));

// Platform / marketplace (B2C)
const RegisterPage = page(
  () => import("./pages/platform/register"),
  "RegisterPage",
);
const VerifyEmailPage = page(
  () => import("./pages/platform/verify-email"),
  "VerifyEmailPage",
);
const PlatformProfilePage = page(
  () => import("./pages/platform/profile"),
  "PlatformProfilePage",
);
const PlatformDashboardPage = page(
  () => import("./pages/platform/dashboard"),
  "PlatformDashboardPage",
);
// B2C marketplace (doc 17 §6.1)
const MarketplacePage = page(
  () => import("./pages/marketplace/index"),
  "MarketplacePage",
);
const MarketplaceMinePage = page(
  () => import("./pages/marketplace/mine"),
  "MarketplaceMinePage",
);
const MarketplaceServicePage = page(
  () => import("./pages/marketplace/service"),
  "MarketplaceServicePage",
);
const MarketplaceSubscriptionDetailPage = page(
  () => import("./pages/marketplace/subscriptionDetail"),
  "MarketplaceSubscriptionDetailPage",
);

// Provider (B2B) — single-version pages (formerly the "v2" surface).
const CataloguePage = page(
  () => import("./pages/provider/catalogue/index"),
  "CataloguePage",
);
const CatalogueServiceNewPage = page(
  () => import("./pages/provider/catalogue/serviceForm"),
  "CatalogueServiceNewPage",
);
const CatalogueServiceEditPage = page(
  () => import("./pages/provider/catalogue/serviceForm"),
  "CatalogueServiceEditPage",
);
const CataloguePackageNewPage = page(
  () => import("./pages/provider/catalogue/packageForm"),
  "CataloguePackageNewPage",
);
const CataloguePackageEditPage = page(
  () => import("./pages/provider/catalogue/packageForm"),
  "CataloguePackageEditPage",
);
const ServiceFormPreviewsPage = page(
  () => import("./pages/provider/catalogue/serviceFormPreviews"),
  "ServiceFormPreviewsPage",
);
const DashboardPage = page(
  () => import("./pages/provider/dashboard/index"),
  "DashboardPage",
);
const ReviewsPage = page(
  () => import("./pages/provider/reviews/index"),
  "ReviewsPage",
);
const ClientsPage = page(
  () => import("./pages/provider/clients/index"),
  "ClientsPage",
);
const ClientDetailPage = page(
  () => import("./pages/provider/clients/detail"),
  "ClientDetailPage",
);
const SubscriptionsPage = page(
  () => import("./pages/provider/subscriptions/index"),
  "SubscriptionsPage",
);
const SubscriptionDetailPage = page(
  () => import("./pages/provider/subscriptions/detail"),
  "SubscriptionDetailPage",
);
const SubscriptionNewPage = page(
  () => import("./pages/provider/subscriptions/new"),
  "SubscriptionNewPage",
);
const BillingPage = page(
  () => import("./pages/provider/billing/index"),
  "BillingPage",
);
const BillingDetailPage = page(
  () => import("./pages/provider/billing/detail"),
  "BillingDetailPage",
);
const BillingDashboardPage = page(
  () => import("./pages/provider/billing/dashboard"),
  "BillingDashboardPage",
);
const BillingRunPage = page(
  () => import("./pages/provider/billing/run"),
  "BillingRunPage",
);
const BillingRunsPage = page(
  () => import("./pages/provider/billing/runs"),
  "BillingRunsPage",
);
const BillingRunDetailPage = page(
  () => import("./pages/provider/billing/runDetail"),
  "BillingRunDetailPage",
);
const AnalyticsPage = page(
  () => import("./pages/provider/analytics/index"),
  "AnalyticsPage",
);
const ReportsIndexPage = page(
  () => import("./pages/provider/reports/index"),
  "ReportsIndexPage",
);
const ReportViewPage = page(
  () => import("./pages/provider/reports/view"),
  "ReportViewPage",
);
const AuditPage = page(
  () => import("./pages/provider/audit/index"),
  "AuditPage",
);
const NotificationsPage = page(
  () => import("./pages/provider/notifications/index"),
  "NotificationsPage",
);
const JobsPage = page(() => import("./pages/provider/jobs/index"), "JobsPage");
const SettingsPage = page(
  () => import("./pages/provider/settings/index"),
  "SettingsPage",
);
const TeamPage = page(() => import("./pages/provider/team/index"), "TeamPage");
const ProviderProfilePage = page(
  () => import("./pages/provider/profile"),
  "ProviderProfilePage",
);

// Client portal (shared by B2B clients + B2C self-registered subscribers)
const PortalDashboardPage = page(
  () => import("./pages/portal/dashboard/index"),
  "PortalDashboardPage",
);
const PortalRecommendationsPage = page(
  () => import("./pages/portal/recommendations/index"),
  "PortalRecommendationsPage",
);
const PortalSubscriptionsPage = page(
  () => import("./pages/portal/subscriptions/index"),
  "PortalSubscriptionsPage",
);
const PortalBrowsePage = page(
  () => import("./pages/portal/subscriptions/browse"),
  "PortalBrowsePage",
);
const PortalSubscriptionDetailPage = page(
  () => import("./pages/portal/subscriptions/detail"),
  "PortalSubscriptionDetailPage",
);
const PortalBillingPage = page(
  () => import("./pages/portal/billing/index"),
  "PortalBillingPage",
);
const PortalBillingDetailPage = page(
  () => import("./pages/portal/billing/detail"),
  "PortalBillingDetailPage",
);
const PortalNotificationsPage = page(
  () => import("./pages/portal/notifications/index"),
  "PortalNotificationsPage",
);
const PortalReportsIndexPage = page(
  () => import("./pages/portal/reports/index"),
  "PortalReportsIndexPage",
);
const ClientTeamPage = page(
  () => import("./pages/portal/team"),
  "ClientTeamPage",
);
const PortalProfilePage = page(
  () => import("./pages/portal/profile"),
  "ClientProfilePage",
);
const PortalMarketplacePage = page(
  () => import("./pages/portal/marketplace"),
  "PortalMarketplacePage",
);
const PortalMarketplaceServiceDetailPage = page(
  () => import("./pages/portal/marketplaceServiceDetail"),
  "PortalMarketplaceServiceDetailPage",
);

const rootRoute = createRootRoute({
  component: () => (
    <Suspense
      fallback={<div className="p-10 text-muted-foreground">Loading…</div>}
    >
      <Outlet />
    </Suspense>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: RootRedirect,
});
// Neutral login entry points (AUTH-01..04). The path is a HINT only — post-login
// routing is by the identity's resolved capability + host, not which door was used
// (AUTH-05, handled in RootRedirect). Each door presents its audience's neutral,
// app-branded front (AMSaaS vs AMCaaS); the two provider doors share a path and
// are picked by host at runtime (see pages/auth/dispatch).
// Every login door is gated on an explicitly-resolved app host (AppHostGate): real
// amcaas*/amsaas* hostname, or a dev ?app= override. A bare localhost/login with no
// override shows the "choose an app" notice instead of defaulting to a door.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => (
    <AppHostGate>
      <DefaultLoginPage />
    </AppHostGate>
  ),
});
const loginClientRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login/client",
  component: () => (
    <AppHostGate>
      <AmsaasClientLoginPage />
    </AppHostGate>
  ),
});
const loginProviderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login/provider",
  component: () => (
    <AppHostGate>
      <ProviderLoginPage />
    </AppHostGate>
  ),
});
const loginPlatformRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login/platform",
  component: () => (
    <AppHostGate>
      <AmcaasPlatformLoginPage />
    </AppHostGate>
  ),
});

// ENH-007 platform (self-service) public routes — neutral, no org.
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  component: RegisterPage,
});
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/verify-email",
  component: VerifyEmailPage,
});
const platformProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/profile",
  component: PlatformProfilePage,
});
const platformDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform/dashboard",
  component: PlatformDashboardPage,
});
const marketplaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/marketplace",
  component: MarketplacePage,
});
const marketplaceMineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/marketplace/mine",
  component: MarketplaceMinePage,
});
// Declared before the service route so the more specific /marketplace/mine/$id is
// registered alongside its parent list rather than shadowed by it.
const marketplaceMineDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/marketplace/mine/$id",
  component: MarketplaceSubscriptionDetailPage,
});
const marketplaceServiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/marketplace/service/$id",
  component: MarketplaceServicePage,
});

// Provider portal
const providerLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/provider",
  component: ProviderLayout,
});
const pr = <const TPath extends string>(
  path: TPath,
  component: LazyExoticComponent<FC>,
) =>
  createRoute({ getParentRoute: () => providerLayoutRoute, path, component });

const providerIndexRoute = createRoute({
  getParentRoute: () => providerLayoutRoute,
  path: "/",
  component: () => <Navigate to="/provider/dashboard" />,
});

// v2 Service editor — `tab` search param makes the active tab deep-linkable and
// survive refresh/back (validated to a known tab id).
const CATALOGUE__SERVICE_TABS = [
  "details",
  "pricing",
  "billing",
  "addons",
  "visibility",
] as const;
const catalogueServiceEditRoute = createRoute({
  getParentRoute: () => providerLayoutRoute,
  path: "catalogue/services/$id",
  component: CatalogueServiceEditPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: (typeof CATALOGUE__SERVICE_TABS)[number] } => {
    const tab = search.tab;
    return typeof tab === "string" &&
      (CATALOGUE__SERVICE_TABS as readonly string[]).includes(tab)
      ? { tab: tab as (typeof CATALOGUE__SERVICE_TABS)[number] }
      : {};
  },
});

const CATALOGUE__PACKAGE_TABS = [
  "details",
  "pricing",
  "billing",
  "services",
  "visibility",
] as const;
const cataloguePackageEditRoute = createRoute({
  getParentRoute: () => providerLayoutRoute,
  path: "catalogue/packages/$id",
  component: CataloguePackageEditPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: (typeof CATALOGUE__PACKAGE_TABS)[number] } => {
    const tab = search.tab;
    return typeof tab === "string" &&
      (CATALOGUE__PACKAGE_TABS as readonly string[]).includes(tab)
      ? { tab: tab as (typeof CATALOGUE__PACKAGE_TABS)[number] }
      : {};
  },
});

// Invoice register — `billingRunId` deep-links the list to one run's output, which is
// how the run-detail page's "View N invoices" action navigates (doc 23 §9).
const billingIndexRoute = createRoute({
  getParentRoute: () => providerLayoutRoute,
  path: "billing",
  component: BillingPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { billingRunId?: string } => {
    const id = search.billingRunId;
    return typeof id === "string" && id.length > 0 ? { billingRunId: id } : {};
  },
});

// Single-version provider routes — the former v2 pages ARE the app now (clean paths,
// no `-v2`). v1-only pages with no v2 successor (Profile) are kept.
const providerRoutes = [
  providerIndexRoute,
  pr("dashboard", DashboardPage),
  pr("catalogue", CataloguePage),
  pr("catalogue/services/new", CatalogueServiceNewPage),
  catalogueServiceEditRoute,
  pr("catalogue/packages/new", CataloguePackageNewPage),
  cataloguePackageEditRoute,
  pr("catalogue/form-previews", ServiceFormPreviewsPage),
  pr("clients", ClientsPage),
  pr("clients/$id", ClientDetailPage),
  pr("subscriptions", SubscriptionsPage),
  pr("subscriptions/new", SubscriptionNewPage),
  pr("subscriptions/$id", SubscriptionDetailPage),
  pr("reviews", ReviewsPage),
  billingIndexRoute,
  pr("billing/dashboard", BillingDashboardPage),
  pr("billing/run", BillingRunPage),
  pr("billing/runs", BillingRunsPage),
  pr("billing/runs/$id", BillingRunDetailPage),
  pr("billing/$id", BillingDetailPage),
  pr("analytics", AnalyticsPage),
  pr("reports", ReportsIndexPage),
  pr("reports/$reportKey", ReportViewPage),
  pr("audit", AuditPage),
  pr("notifications", NotificationsPage),
  pr("jobs", JobsPage),
  pr("settings", SettingsPage),
  pr("team", TeamPage),
  pr("profile", ProviderProfilePage),
] as const;

// Client portal
const portalLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/portal",
  component: ClientLayout,
});
const po = <const TPath extends string>(
  path: TPath,
  component: LazyExoticComponent<FC>,
) => createRoute({ getParentRoute: () => portalLayoutRoute, path, component });

const portalIndexRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/",
  component: () => <Navigate to="/portal/dashboard" />,
});
// Single-version portal routes. v1-only pages with no v2 successor (Marketplace,
// Team, Profile) are kept.
const portalRoutes = [
  portalIndexRoute,
  po("dashboard", PortalDashboardPage),
  po("recommendations", PortalRecommendationsPage),
  po("subscriptions", PortalSubscriptionsPage),
  po("subscriptions/browse", PortalBrowsePage),
  po("subscriptions/$id", PortalSubscriptionDetailPage),
  po("billing", PortalBillingPage),
  po("billing/$id", PortalBillingDetailPage),
  po("reports", PortalReportsIndexPage),
  po("reports/$reportKey", ReportViewPage),
  po("notifications", PortalNotificationsPage),
  po("marketplace", PortalMarketplacePage),
  po("marketplace/services/$id", PortalMarketplaceServiceDetailPage),
  po("team", ClientTeamPage),
  po("profile", PortalProfilePage),
] as const;

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  loginClientRoute,
  loginProviderRoute,
  loginPlatformRoute,
  registerRoute,
  verifyEmailRoute,
  platformProfileRoute,
  platformDashboardRoute,
  marketplaceRoute,
  marketplaceMineRoute,
  marketplaceMineDetailRoute,
  marketplaceServiceRoute,
  providerLayoutRoute.addChildren(providerRoutes),
  portalLayoutRoute.addChildren(portalRoutes),
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundPage,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
